import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

import { SearchProvider } from "./context/SearchContext";
import { CampaignProvider } from "./context/CampaignContext";
import AuthInitializer from "./components/auth/AuthInitializer";

// Pages
import HomePage from "./pages/HomePage.jsx";
// import LoginPage from "./pages/LoginPage.jsx";
import SearchResultsPage from "./pages/SearchResultsPage.jsx";
import DashboardPage from "./pages/DashboardPage.jsx";
import { CampaignNewPageWrapper } from "./pages/CampaignNewPage.jsx";
import CampaignsPageWrapper from "./pages/CampaignsPage.jsx";
import CampaignDetailPage from "./pages/CampaignDetailPage.jsx";
import AnalyticsPage from "./pages/AnalyticsPage.jsx";
import AccountPage from "./pages/AccountPage.jsx";
import NotFound from "./pages/NotFound.jsx";
import PricingPlan from "./pages/PricingPlan.jsx";
import EmailTemplateManager from "./pages/EmailTemplateManager.jsx";
import PaymentSuccess from "./pages/payment/PaymentSuccess.jsx";
import PaymentFailure from "./pages/payment/PaymentFailure.jsx";
import TermsAndCondition from "./components/TermandCondition/TermsAndCondition.jsx";
import PrivacyPolicy from "./components/TermandCondition/PrivacyPolicy.jsx";
import CancellationAndRefund from "./components/TermandCondition/CancellationAndRefund.jsx";
import ContactUs from "./pages/ContactUs.jsx";
import AboutUs from "./pages/AboutUspage.jsx";
import FAQPage from "./pages/FAQPage.jsx";
import { OAuthPopup } from "./components/campaigns/OAuthPopup.jsx";
import CreateCampaignStepper from "./pages/sendEmail/CreateCampaignStepper.jsx";
import EmailConnectModal from "./components/campaigns/EmailConnectModal.jsx";
import CustomerTestimonialPage from "./pages/testimonial/CustomerTestimonialPage.jsx";
import OAuthStatus from "./components/campaigns/OAuthStatus.jsx";
import PricingPalnWithoutToken from "./pages/pricingPlan/PricingPalnWithoutToken.jsx";
import NormalLoader from "./components/customeLoader/NormalLoader.jsx";
import ThankYouPage from "./pages/thankYou/ThankyouPage.jsx";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { fetchUserDetails } from "./Redux/ReduxSlices/profileSlice.js";
import PaymentRequiredModal from "./components/paymentModal/PaymentRequiredModal.jsx";
import HowToUse from "./pages/howToUse/HowToUse.jsx";
import MaxiPitchBenefits from "./components/common/MaxiPitchBenefits.jsx";
import BlogPage from "./components/blogPage/BlogPage.jsx";
import BlogDetailPage from "./components/blogPage/BlogDetailPage.jsx";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { fetchIpData } from "./Redux/ReduxSlices/newsearchSlice.js";
import { DialogDescription, DialogTitle } from "./components/ui/dialog.jsx";
import MyDownloads from "./components/downloadData/MyDownloads.jsx";
// import CaseStudiesPage from "./components/caseStudies/CaseStudiesPage.jsx";
// import CaseStudyDetailPage from "./components/caseStudies/CaseStudyDetailPage.jsx";
// import ThankyouPage from "./pages/thankyou/ThankyouPage.jsx";

const queryClient = new QueryClient();

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, tokenValidated } = useAuth();
  const location = useLocation();

  // Wait for token validation to complete
  if (!tokenValidated) {
    return <NormalLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const App = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.profile?.details);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { isAuthenticated, tokenValidated } = useAuth();
  const profile = useSelector((state) => state.profile.details);

  useEffect(() => {
    dispatch(fetchUserDetails());
  }, [dispatch]);

  useEffect(() => {
    if (userData && !localStorage.getItem("loginAt")) {
      localStorage.setItem("loginAt", Date.now());
    }
  }, [userData]);

  const hasPaymentRequired =
    userData?.payment_gate?.has_payment_required === true &&
    profile?.data?.credit < 1;

  useEffect(() => {
    let timer;

    if (hasPaymentRequired && location.pathname !== "/pricing") {
      timer = setTimeout(() => {
        setShowPaymentModal(true);
      }, 20000); // 40 seconds
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [hasPaymentRequired]);

  useEffect(() => {
    // 1️⃣ Check if country exists in localStorage
    const savedCountry = localStorage.getItem("user_country_name");

    if (savedCountry) {
      return; // ⛔ Do not call API again
    }

    // 2️⃣ No saved country? Fetch from API and store it.
    dispatch(fetchIpData()).then((res) => {
      const country = res?.payload?.data?.country_name;
      if (country) {
        localStorage.setItem("user_country_name", country);
      }
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthInitializer />
        <CampaignProvider>
          <SearchProvider>
            <Toaster
              position="top-right"
              offset={{ top: 50 }}
              richColors
              toastOptions={{
                classNames: {
                  success: "toast-success",
                  error: "toast-error",
                  warning: "toast-warning",
                },
              }}
            />
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              {hasPaymentRequired && (
                <Dialog
                  open={showPaymentModal}
                  onOpenChange={setShowPaymentModal}
                >
                  <DialogContent className="w-full max-w-lg max-md:w-80">
                    {/* Hidden accessibility elements */}
                    <DialogTitle className="sr-only">
                      {/* Payment Required */}
                    </DialogTitle>

                    <DialogDescription className="sr-only">
                      {/* Upgrade your plan to continue using MaxiPitch features. */}
                    </DialogDescription>

                    <PaymentRequiredModal
                      hasPaymentRequired={hasPaymentRequired}
                      setShowModal={setShowPaymentModal}
                    />
                  </DialogContent>
                </Dialog>
              )}
              <Routes>
                <Route
                  path="/"
                  element={<HomePage isAuthenticated={isAuthenticated} />}
                />
                <Route path="/search" element={<SearchResultsPage />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/campaign/new"
                  element={
                    <ProtectedRoute>
                      <CampaignNewPageWrapper />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/campaigns"
                  element={
                    <ProtectedRoute>
                      <CampaignsPageWrapper />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/campaign/:id"
                  element={
                    <ProtectedRoute>
                      <CampaignDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <AnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <AccountPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <AccountPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <ProtectedRoute>
                      <AccountPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/email-template-manager"
                  element={
                    <ProtectedRoute>
                      <EmailTemplateManager />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pricing"
                  element={
                    <ProtectedRoute>
                      <PricingPlan />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/send-emails"
                  element={
                    <ProtectedRoute>
                      <CreateCampaignStepper />{" "}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/addemails"
                  element={
                    <ProtectedRoute>
                      <EmailConnectModal asPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-success"
                  element={
                    <ProtectedRoute>
                      <PaymentSuccess />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-failure"
                  element={
                    <ProtectedRoute>
                      <PaymentFailure />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-downloads"
                  element={
                    <ProtectedRoute>
                      <MyDownloads />
                    </ProtectedRoute>
                  }
                />
                {/* <Route path="/login" element={<LoginPage />} /> */}
                <Route path="/signup" element={<HomePage />} />
                <Route path="/oauth-status" element={<OAuthStatus />} />
                <Route
                  path="/terms-and-Conditions"
                  element={<TermsAndCondition />}
                />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route
                  path="/cancellation-and-refund"
                  element={<CancellationAndRefund />}
                />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route
                  path="/about-us"
                  element={<AboutUs isAuthenticated={isAuthenticated} />}
                />
                <Route path="/faq" element={<FAQPage />} />
                <Route
                  path="/testimonial"
                  element={<CustomerTestimonialPage />}
                />
                <Route
                  path="/pricing-plans"
                  element={<PricingPalnWithoutToken />}
                />

                {/* <Route
                  path="/thank-you-waitinglist"
                  element={<ThankYouPage />}
                /> */}

                <Route path="*" element={<NotFound />} />
                <Route path="/how-to-use" element={<HowToUse />} />
                <Route
                  path="/maxipitch-benefits"
                  element={<MaxiPitchBenefits />}
                />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:id" element={<BlogDetailPage />} />
                {/* <Route path="/case-studies" element={<CaseStudiesPage />} />
                <Route path="/case-studies/:id" element={<CaseStudyDetailPage />} /> */}
              </Routes>
            </BrowserRouter>
          </SearchProvider>
        </CampaignProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
